/**
 * Rows that a person adds and removes — quotation lines, agreement lines,
 * work-order services, invoice lines, payment allocations.
 *
 * A ROW'S KEY IS ITS OWN, NEVER ITS POSITION. Keyed by position, removing the
 * second of three rows made React hand the second row's inputs to the third:
 * the screen kept showing the removed row's service while the form state held
 * the other one, so what was saved (and priced, and totalled) was not what
 * the desk saw. A key minted once per row, kept for the row's whole life, and
 * used for every update and removal makes that impossible.
 *
 * Any row may be removed, the last one included: an empty list is a state the
 * form shows honestly ("nothing added yet"), and the server decides whether a
 * record may be saved without lines.
 */
let seed = 0;

/** A key for a new row. Unique for the life of the page; never reused. */
export function newRowKey(prefix = 'row'): string {
  seed += 1;
  return `${prefix}-${seed}`;
}

/** Remove one row by its key. */
export function withoutRow<Row extends { readonly key: string }>(
  rows: readonly Row[],
  key: string,
): Row[] {
  return rows.filter((row) => row.key !== key);
}

/** Change one row by its key. */
export function patchRow<Row extends { readonly key: string }>(
  rows: readonly Row[],
  key: string,
  change: Partial<Omit<Row, 'key'>>,
): Row[] {
  return rows.map((row) => (row.key === key ? { ...row, ...change } : row));
}
